import { Order } from '../../shared/models/Order';

export interface OrderBoletaExportStatus {
  orderId: number;
  tieneBoleta: boolean;
  boletaId?: number;
  numeroCompleto?: string;
  envioCliente?: 'PENDIENTE' | 'ENVIADO' | string;
}

function uniqueJoined(values: Array<string | null | undefined>): string {
  const names = values
    .map(v => (v == null ? '' : String(v).trim()))
    .filter(v => v.length > 0 && v !== '—');
  const unique = [...new Set(names)];
  return unique.length ? unique.join(', ') : '—';
}

/** Nombre(s) de producto de la orden (no categoría). */
export function getOrderProductLabel(order: Order | null | undefined): string {
  if (!order?.items?.length) return '—';
  return uniqueJoined(
    order.items.map((i: any) =>
      i?.product?.productName ||
      i?.product?.name ||
      i?.productName ||
      i?.description ||
      null
    )
  );
}

export function getItemCategoryLabel(item: any): string {
  return uniqueJoined([
    item?.categoryName,
    item?.categoria,
    item?.product?.categoryName,
    item?.product?.modelo?.categoryName,
    item?.product?.modelo?.category?.name,
    item?.product?.category?.name
  ]);
}

export function getItemModeloLabel(item: any): string {
  return uniqueJoined([
    item?.modeloName,
    item?.modelName,
    item?.product?.modeloName,
    item?.product?.modelo?.name,
    item?.product?.modelName
  ]);
}

export function getOrderCategoryLabel(order: Order | null | undefined): string {
  if (!order?.items?.length) return '—';
  return uniqueJoined(order.items.map((i: any) => getItemCategoryLabel(i)));
}

export function getOrderModeloLabel(order: Order | null | undefined): string {
  if (!order?.items?.length) return '—';
  return uniqueJoined(order.items.map((i: any) => getItemModeloLabel(i)));
}

export function getOrderItemsCount(order: Order | null | undefined): number {
  if (!order?.items?.length) return 0;
  return order.items.reduce((sum: number, i: any) => sum + (Number(i?.quantity) || 0), 0);
}

export function getOrderDeliveryPrice(order: Order | null | undefined): number {
  const n = Number(order?.deliveryPrice);
  return Number.isFinite(n) ? n : 0;
}

export function getOrderAmountPaid(order: Order | null | undefined): number {
  const n = Number(order?.amountPaid);
  return Number.isFinite(n) ? n : 0;
}

export function getOrderMpFee(order: Order | null | undefined): number {
  const n = Number(order?.mpFeeAmount);
  return Number.isFinite(n) ? n : 0;
}

export function getOrderNetCollected(order: Order | null | undefined): number {
  if (order?.mpNetAmount == null) return getOrderAmountPaid(order);
  const n = Number(order.mpNetAmount);
  return Number.isFinite(n) ? n : getOrderAmountPaid(order);
}

export function getOrderTotalAmount(order: Order | null | undefined): number {
  const n = Number(order?.totalAmount);
  return Number.isFinite(n) ? n : 0;
}

/** Texto para export: Sin boleta | Emitida Nº | Emitida y enviada Nº */
export function getBoletaExportLabel(
  orderId: number,
  statusMap: Record<number, OrderBoletaExportStatus> | null | undefined
): string {
  const st = statusMap?.[orderId];
  if (!st?.tieneBoleta) return 'Sin boleta';
  const num = st.numeroCompleto || '';
  if (st.envioCliente === 'ENVIADO') {
    return num ? `Emitida y enviada (${num})` : 'Emitida y enviada';
  }
  return num ? `Emitida (${num})` : 'Emitida';
}

export function money(n: number): string {
  return `S/ ${(Number(n) || 0).toFixed(2)}`;
}
